import { z } from "zod";
/**
 * Event taxonomy — the append-only measurement record.
 *
 * Design rules that are contracts, not preferences (v2 §18, §22, ADR-002):
 *
 *  1. Every event carries `appVersion` AND `bundleVersion` AND (when task-scoped)
 *     `taskVersion`. The server NEVER assumes the client is current. Under TWA the
 *     web app updates without user action, so a child could otherwise silently cross
 *     a scoring change mid-trajectory. See ADR-002 bundle pinning.
 *
 *  2. Timing is measured with a monotonic clock (`tSessionMs`, from performance.now()
 *     at session start), not wall clock. Device wall clocks are wrong often enough to
 *     reorder events. `tWallIso` is kept for human debugging only and is NEVER used
 *     for ordering or for any derived measure.
 *
 *  3. `seq` is monotonic per session so the server detects gaps exactly rather than
 *     inferring them. Under IndexedDB eviction (ADR-002) a gap is expected; the
 *     session is marked incomplete, not discarded (v2 §22).
 *
 *  4. Nothing here identifies a device or a person beyond a coarse capability class.
 *     Behavioural telemetry about a named child is already sensitive; adding a
 *     fingerprint would make it worse for no measurement gain (v2 §32).
 */
export const PROTOCOL_VERSION = 1;
/** Coarse, deliberately non-identifying device capability class. */
export const DeviceProfileSchema = z.object({
    /** CSS pixel viewport, rounded to 10px to blunt fingerprinting. */
    viewportW: z.number().int().positive(),
    viewportH: z.number().int().positive(),
    /** Rounded to one decimal. */
    devicePixelRatio: z.number().positive(),
    /** Estimated from rAF sampling. Informational only — RT constructs are excluded (PC-1). */
    refreshRateEstHz: z.number().positive().nullable(),
    /** navigator.hardwareConcurrency, bucketed. */
    cpuClass: z.enum(["low", "mid", "high", "unknown"]),
    /** navigator.deviceMemory in GB where exposed, bucketed. */
    memoryClass: z.enum(["low", "mid", "high", "unknown"]),
    maxTouchPoints: z.number().int().nonnegative(),
    prefersReducedMotion: z.boolean(),
    /** How this session is being delivered — see ADR-002. */
    shell: z.enum(["twa", "pwa", "capacitor", "browser"]),
    /** Engine family only. Never the full UA string. */
    engine: z.enum(["blink", "webkit", "gecko", "unknown"]),
});
/**
 * Signals that reduce confidence in a session (v2 §22 — Goodhart / contamination).
 * These NEVER trigger silent deletion. They lower a validity weight computed at
 * ingest, because "discard" and "trust fully" are both wrong answers.
 */
export const ValidityFlagSchema = z.enum([
    /** App lost focus mid-trial — interruption, or an adult taking over. */
    "blur_during_trial",
    /** Response faster than a child can plausibly perceive and act. */
    "implausible_speed",
    /** Touch pattern inconsistent with one small hand. */
    "multi_touch_anomaly",
    /** Long unexplained gap inside a trial. */
    "long_gap",
    /** In-app help was used on a scored trial. */
    "help_used",
    /** Session-start declaration did not match the active profile. */
    "declared_player_mismatch",
    /** Events missing — expected under storage eviction (ADR-002). */
    "sequence_gap",
    /** Client reported a bundle version other than the pinned one. */
    "bundle_pin_violation",
]);
export const EventKindSchema = z.enum([
    "session_start",
    "session_end",
    "task_start",
    "task_end",
    "trial_start",
    "trial_response",
    "trial_timeout",
    "difficulty_change",
    "choice_made",
    "replay_requested",
    "visibility_change",
    "input_anomaly",
    "help_requested",
    "storage_persist_result",
]);
/** Envelope shared by every event. */
const EnvelopeShape = {
    /** Client-generated UUID. Ingest is idempotent on this — retries are safe. */
    eventId: z.string().uuid(),
    sessionId: z.string().uuid(),
    profileId: z.string().uuid(),
    /** Monotonic within session, starting at 0. Gaps are detectable, not inferred. */
    seq: z.number().int().nonnegative(),
    /** Milliseconds since session start, monotonic clock. */
    tSessionMs: z.number().nonnegative(),
    /** Wall clock. Debugging only — never used for ordering or measurement. */
    tWallIso: z.string().datetime(),
    appVersion: z.string().min(1),
    bundleVersion: z.string().min(1),
    taskId: z.string().min(1).nullable(),
    taskVersion: z.string().min(1).nullable(),
    protocolVersion: z.literal(PROTOCOL_VERSION),
};
const ev = (kind, payload) => z.object({ ...EnvelopeShape, kind: z.literal(kind), payload });
export const SessionStartEventSchema = ev("session_start", z.object({
    device: DeviceProfileSchema,
    /** Result of navigator.storage.persist() — a denied grant changes eviction risk. */
    storagePersisted: z.boolean(),
    /** Who the app was told is playing. Used only to flag mismatch, never to accuse. */
    declaredPlayer: z.enum(["child", "child_with_adult", "unknown"]),
    /** The bundle the SERVER pinned this profile to (ADR-002). */
    pinnedBundleVersion: z.string().min(1),
}));
export const SessionEndEventSchema = ev("session_end", z.object({
    reason: z.enum(["completed", "abandoned", "interrupted", "error"]),
    tasksCompleted: z.number().int().nonnegative(),
}));
export const TaskStartEventSchema = ev("task_start", z.object({
    /** Which alternate form was drawn — practice-effect control (`[FACT: S14]`). */
    formId: z.string().min(1),
    startingDifficulty: z.number().int().nonnegative(),
    /** A-13 lever: was this task presented inside a narrative wrapper? */
    narrative: z.boolean(),
    /**
     * The calibration this trial was measured against.
     *
     * Optional only so protocol v1 recordings stay readable; every client emits it.
     *
     * Without it a calibration bump is unenforceable. `calibrationVersion` exists precisely so
     * that a cosmetic change can bump the task `version` while comparability survives — but
     * the events table stores `taskId` and `taskVersion`, so nothing in a recorded trial said
     * which calibration produced it. Bumping a task's calibration would have pooled old and
     * new trials into one estimate, and every row would have looked perfectly well-formed.
     */
    calibrationVersion: z.string().min(1).optional(),
    /**
     * What this run was FOR, and who asked for it (ADR-011).
     *
     * The teacher's central question is "who practised what", and without these two fields the
     * only way to answer it is to map every `(taskId, difficulty)` pair back into the level
     * windows that objectives are defined over. That inference double-counts wherever two
     * objectives share a level, and it silently attributes free play to whatever objective
     * happens to cover the level the child wandered into.
     *
     * Recorded instead of inferred, and optional because free play is genuinely for nothing in
     * particular. Absent means the child chose the game themselves, which is its own finding.
     */
    objectiveId: z.string().min(1).optional(),
    assignmentId: z.string().uuid().optional(),
}));
export const TaskEndEventSchema = ev("task_end", z.object({
    reason: z.enum(["completed", "abandoned", "ceiling", "floor", "error"]),
    trialsAttempted: z.number().int().nonnegative(),
    /** Raw task-local outcome. Construct estimation happens server-side, never here. */
    rawScore: z.number(),
}));
export const TrialStartEventSchema = ev("trial_start", z.object({
    trialIndex: z.number().int().nonnegative(),
    difficulty: z.number().int().nonnegative(),
    /**
     * Stimulus as presented, so scoring is reproducible from the record alone.
     * For a span task this is the sequence; for a matching task it is the target
     * pattern.
     */
    stimulus: z.array(z.number().int()),
    /**
     * Matching tasks only: the candidate patterns offered, and which one was correct.
     *
     * Added as optional fields rather than by flattening into `stimulus`, because a
     * flattened encoding could not be re-scored by reading the record — and the whole
     * point of storing the stimulus is that a trial stays interpretable years later
     * without the client build that produced it. Absent for span tasks.
     */
    options: z.array(z.array(z.number().int())).optional(),
    correctIndex: z.number().int().nonnegative().optional(),
}));
export const TrialResponseEventSchema = ev("trial_response", z.object({
    trialIndex: z.number().int().nonnegative(),
    response: z.array(z.number().int()),
    correct: z.boolean(),
    /** Time from stimulus end to response completion. NOT a processing-speed measure
     *  (PC-1) — retained for contamination detection only. */
    responseMs: z.number().nonnegative(),
    /**
     * Self-corrections inside this trial: a placement undone, a choice changed before
     * committing.
     *
     * Process data, and the kind that a right/wrong score throws away — two children can both
     * finish a shape correctly while one built it straight through and the other rearranged it
     * four times. Those are not the same performance.
     *
     * Optional because most tasks have no way to undo: a span trial's taps are committed as they
     * happen. Absent means "the task offered no correction", NOT "the child made none", and the
     * distinction matters because reading absence as zero would make span trials look like
     * flawless construction.
     */
    corrections: z.number().int().nonnegative().optional(),
    /**
     * Times the child COMMITTED an answer within this trial, where the task allows more than one.
     *
     * Distinct from `corrections`, and the distinction is the whole point: corrections are edits
     * made before committing, attempts are commits. A child who queues a robot program, runs it,
     * watches it fail, adjusts and runs again has one correction and two attempts — and a child
     * who reasons it out and runs once has neither. Both may reach the goal.
     *
     * This is the clearest signal available for whether an answer was planned or found by trying,
     * which no accuracy score contains. Optional because most tasks commit once: absent means the
     * task offered no second attempt, NOT that the child needed none.
     */
    attempts: z.number().int().nonnegative().optional(),
}));
export const TrialTimeoutEventSchema = ev("trial_timeout", z.object({
    trialIndex: z.number().int().nonnegative(),
    elapsedMs: z.number().nonnegative(),
}));
export const DifficultyChangeEventSchema = ev("difficulty_change", z.object({
    from: z.number().int().nonnegative(),
    to: z.number().int().nonnegative(),
    reason: z.enum(["correct_streak", "error", "child_choice"]),
}));
export const ChoiceMadeEventSchema = ev("choice_made", z.object({
    /**
     * Free choice between offered activities.
     *
     * Kept after the pivot, and it matters more now than it did: the child's home screen leads
     * with what the teacher assigned, so a recorded choice is a child stepping around that. What
     * they pick when nothing is asked of them is the one thing an assignment cannot tell you.
     */
    offered: z.array(z.string().min(1)),
    chosen: z.string().min(1),
}));
export const ReplayRequestedEventSchema = ev("replay_requested", z.object({ ofTaskId: z.string().min(1) }));
export const VisibilityChangeEventSchema = ev("visibility_change", z.object({
    visible: z.boolean(),
    /** True when this happened inside a scored trial → validity flag at ingest. */
    duringTrial: z.boolean(),
}));
export const InputAnomalyEventSchema = ev("input_anomaly", z.object({
    anomaly: z.enum(["implausible_speed", "multi_touch", "off_target_burst"]),
    detail: z.string().max(200),
}));
export const HelpRequestedEventSchema = ev("help_requested", z.object({ duringTrial: z.boolean() }));
export const StoragePersistResultEventSchema = ev("storage_persist_result", z.object({ granted: z.boolean(), estimateBytes: z.number().nonnegative().nullable() }));
export const EventSchema = z.discriminatedUnion("kind", [
    SessionStartEventSchema,
    SessionEndEventSchema,
    TaskStartEventSchema,
    TaskEndEventSchema,
    TrialStartEventSchema,
    TrialResponseEventSchema,
    TrialTimeoutEventSchema,
    DifficultyChangeEventSchema,
    ChoiceMadeEventSchema,
    ReplayRequestedEventSchema,
    VisibilityChangeEventSchema,
    InputAnomalyEventSchema,
    HelpRequestedEventSchema,
    StoragePersistResultEventSchema,
]);
/** Ingest accepts batches. Continuous flushing is required by ADR-002. */
export const EventBatchSchema = z.object({
    protocolVersion: z.literal(PROTOCOL_VERSION),
    events: z.array(EventSchema).min(1).max(200),
});
export const IngestResultSchema = z.object({
    accepted: z.number().int().nonnegative(),
    /** Already-seen eventIds. Idempotent replay is normal, not an error. */
    duplicates: z.number().int().nonnegative(),
    rejected: z.array(z.object({ eventId: z.string(), reason: z.string() })),
});
//# sourceMappingURL=events.js.map